import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Search, ChevronLeft, ChevronRight, FilePlus2, CopyPlus, ExternalLink } from 'lucide-react';
import WorkflowItem, { Workflow } from '../../components/layout/WorkflowItem';
import { WorkflowAPI } from './api';
import { ContextMenu } from './ContextMenu';
import { apiService } from '../../services/api';

const MIN_WIDTH = 220;
const MAX_WIDTH = 500;

interface WorkflowListPanelProps {
  isOpen: boolean;
  onToggle: () => void;
  draftName?: string;
}

export const WorkflowListPanel: React.FC<WorkflowListPanelProps> = ({ isOpen, onToggle, draftName }) => {
  const { t } = useTranslation('workflow');
  const [searchQuery, setSearchQuery] = useState('');
  const [workflows, setWorkflows] = useState<Workflow[]>([]);
  const [panelWidth, setPanelWidth] = useState(288); // w-72 = 288px
  const [ctxMenu, setCtxMenu] = useState<{ x: number; y: number; wf: Workflow } | null>(null);
  const [duplicatingId, setDuplicatingId] = useState<string | null>(null);
  const navigate = useNavigate();
  const params = useParams();
  const activeWorkflowId = params?.id || null;

  const containerRef = useRef<HTMLDivElement>(null);
  const dragStartX = useRef(0);
  const dragStartWidth = useRef(0);
  const didDrag = useRef(false);

  const handleTogglePointerDown = useCallback((e: React.PointerEvent) => {
    if (e.button !== 0) return;
    dragStartX.current = e.clientX;
    dragStartWidth.current = panelWidth;
    didDrag.current = false;
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
  }, [panelWidth]);

  const handleTogglePointerMove = useCallback((e: React.PointerEvent) => {
    if (!e.buttons) return;
    const dx = e.clientX - dragStartX.current;
    if (!didDrag.current && Math.abs(dx) < 5) return;
    didDrag.current = true;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    const containerLeft = containerRef.current?.getBoundingClientRect().left ?? 0;
    const newWidth = Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, e.clientX - containerLeft));
    setPanelWidth(newWidth);
  }, []);

  const handleTogglePointerUp = useCallback(() => {
    if (didDrag.current) {
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    } else {
      onToggle();
    }
    didDrag.current = false;
  }, [onToggle]);

  const fetchWorkflows = useCallback(async () => {
    try {
      const resp: any = await WorkflowAPI.getAll();
      const list = (resp?.workflows ?? resp) || [];
      if (!Array.isArray(list)) return;
      const items: Workflow[] = list.map((w: any) => {
        let accountCount = 0;
        if (w.nodes && Array.isArray(w.nodes)) {
          w.nodes.forEach((node: any) => {
            if (node.type === 'account_selector') {
              const cfg = node.data?.account_selector_config;
              if (cfg && Array.isArray(cfg.selected_accounts)) {
                accountCount += cfg.selected_accounts.length;
              }
            }
          });
        }
        return {
          id: w._id || w.id,
          name: w.name || 'Unnamed Workflow',
          description: w.description || '',
          nodeCount: (w.nodes && w.nodes.length) || w.node_count || 0,
          edgeCount: (w.edges && w.edges.length) || w.edge_count || 0,
          assignedAccounts: accountCount,
          campaignId: w.campaign_id || w.campaignId || null,
          updatedAt: w.updated_at || w.created_at || undefined,
          isActive: w.status === 'active',
          isCompleted: w.status === 'completed',
          runType: (w.config?.run_type || 'recurring') as 'recurring' | 'one_shot',
        };
      });
      setWorkflows(items);
    } catch (err) {
      console.error('Failed to fetch workflows:', err);
    }
  }, []);

  const handleDuplicate = useCallback(async (wf: Workflow) => {
    setCtxMenu(null);
    setDuplicatingId(wf.id);
    try {
      const newName = `${wf.name} ${t('copySuffix')}`;
      const result: any = await WorkflowAPI.duplicate(wf.id, newName);
      await fetchWorkflows();
      const newId = result?._id || result?.id;
      if (newId) navigate(`/workflows/${newId}`);
    } catch (e) {
      console.error('Duplicate workflow failed', e);
    } finally {
      setDuplicatingId(null);
    }
  }, [fetchWorkflows, navigate, t]);

  // Fetch on mount
  useEffect(() => {
    fetchWorkflows();
  }, [fetchWorkflows]);

  // Re-fetch when panel opens
  useEffect(() => {
    if (isOpen) fetchWorkflows();
  }, [isOpen, fetchWorkflows]);

  // Refresh on workflow status changes
  useEffect(() => {
    const handler = () => { if (isOpen) fetchWorkflows(); };
    window.addEventListener('workflow-status-changed', handler);
    return () => window.removeEventListener('workflow-status-changed', handler);
  }, [isOpen, fetchWorkflows]);

  const filtered = workflows.filter((w) =>
    w.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    w.id.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div ref={containerRef} className="flex h-full flex-shrink-0">
      {/* Panel content */}
      <div
        className={`transition-all duration-300 ease-in-out overflow-hidden bg-white border-r border-gray-200 ${isOpen ? '' : 'w-0'}`}
        style={isOpen ? { width: `${panelWidth}px` } : undefined}
      >
        <div style={{ width: `${panelWidth}px` }} className="h-full flex flex-col">
          {/* Search */}
          <div className="px-3 py-3 border-b border-gray-100">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search workflows..."
                className="w-full pl-8 pr-3 py-2 text-sm border border-gray-200 rounded-lg bg-gray-50 focus:bg-white focus:border-blue-300 focus:outline-none transition-colors"
              />
            </div>
          </div>

          {/* List */}
          <div className="flex-1 overflow-y-auto">
            {/* Draft item when creating new workflow */}
            {!activeWorkflowId && draftName && (
              <div className="flex items-center gap-2.5 px-3 py-3 bg-blue-50 border-b border-blue-100">
                <div className="w-9 h-9 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                  <FilePlus2 size={18} className="text-blue-500" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-blue-700 truncate">{draftName || 'Untitled Workflow'}</p>
                  <p className="text-xs text-blue-400">Draft · Not saved</p>
                </div>
              </div>
            )}
            {filtered.length === 0 && !(!activeWorkflowId && draftName) ? (
              <p className="text-center text-gray-400 text-sm py-6">
                {searchQuery ? 'No workflows found' : 'No workflows'}
              </p>
            ) : (
              filtered.map((wf) => (
                <div
                  key={wf.id}
                  onContextMenu={(e) => {
                    e.preventDefault();
                    setCtxMenu({ x: e.clientX, y: e.clientY, wf });
                  }}
                  className="relative"
                >
                  <WorkflowItem
                    workflow={wf}
                    active={wf.id === activeWorkflowId}
                    onClick={() => navigate(`/workflows/${wf.id}`)}
                  />
                  {duplicatingId === wf.id && (
                    <div className="absolute inset-0 bg-white/70 flex items-center justify-center rounded">
                      <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Context menu for right-click on workflow items */}
      {ctxMenu && (
        <ContextMenu
          x={ctxMenu.x}
          y={ctxMenu.y}
          onClose={() => setCtxMenu(null)}
          items={[
            { type: 'header', label: ctxMenu.wf.name },
            {
              label: t('openWorkflow'),
              icon: <ExternalLink className="w-4 h-4" />,
              onClick: () => navigate(`/workflows/${ctxMenu.wf.id}`),
            },
            { type: 'separator' },
            {
              label: t('duplicate'),
              icon: <CopyPlus className="w-4 h-4" />,
              disabled: duplicatingId === ctxMenu.wf.id,
              onClick: () => handleDuplicate(ctxMenu.wf),
            },
          ]}
        />
      )}

      {/* Toggle button — click to collapse, drag to resize.
          `self-start` keeps this wrapper only as tall as the button itself, so its
          (-ml-4) overlap doesn't extend full-height and block the panel's scrollbar. */}
      <div className="relative flex items-start pt-4 -ml-4 z-10 self-start">
        <button
          onPointerDown={handleTogglePointerDown}
          onPointerMove={handleTogglePointerMove}
          onPointerUp={handleTogglePointerUp}
          className="p-1.5 rounded-full bg-white border border-gray-200 hover:border-gray-400 hover:bg-gray-100 hover:shadow-lg transition-all shadow-sm cursor-col-resize touch-none"
          title={isOpen ? 'Click to collapse / Drag to resize' : 'Expand'}
        >
          {isOpen ? (
            <ChevronLeft size={16} className="text-gray-600" />
          ) : (
            <ChevronRight size={16} className="text-gray-600" />
          )}
        </button>
      </div>
    </div>
  );
};
